import React, { useEffect, useState } from "react";
import {
  addAboutCarouselSlide,
  addCampusActivity,
  addShiningStar,
  deleteAboutCarouselSlide,
  deleteCampusActivity,
  deleteShiningStar,
  getAboutCarouselSlides,
  getCampusActivities,
  getShiningStars,
  updateAboutCarouselSlide,
  updateCampusActivity,
  updateShiningStar
} from "../services/api";
import { confirmAppAction, showAppToast } from "../utils/feedback";
import "./GalleryAdminPage.css";

const createStarForm = () => ({
  name: "",
  image: "",
  resultLabel: "Bright Line Showcase",
  stream: "",
  highlight: "",
  posterIndex: "",
  displayOrder: 0,
  isActive: true
});

const createAboutForm = () => ({
  title: "",
  subtitle: "",
  description: "",
  image: "",
  badge: "Bright Line Classes",
  displayOrder: 0,
  isActive: true
});

const createActivityForm = () => ({
  title: "",
  image: "",
  description: "",
  displayOrder: 0,
  isActive: true
});

const readGalleryImageFile = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg"];
    const lowerName = (file.name || "").toLowerCase();
    const hasValidExtension = lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg");
    if (!allowedTypes.includes(file.type) || !hasValidExtension) {
      reject(new Error("Only JPG and JPEG images are allowed"));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.readAsDataURL(file);
  });

function GalleryAdminPage() {
  const [loading, setLoading] = useState(true);
  const [workingKey, setWorkingKey] = useState("");
  const [aboutSlides, setAboutSlides] = useState([]);
  const [shiningStars, setShiningStars] = useState([]);
  const [campusActivities, setCampusActivities] = useState([]);
  const [editingAboutId, setEditingAboutId] = useState("");
  const [editingStarId, setEditingStarId] = useState("");
  const [editingActivityId, setEditingActivityId] = useState("");
  const [aboutForm, setAboutForm] = useState(createAboutForm());
  const [starForm, setStarForm] = useState(createStarForm());
  const [activityForm, setActivityForm] = useState(createActivityForm());
  const [aboutFileKey, setAboutFileKey] = useState(0);
  const [starFileKey, setStarFileKey] = useState(0);
  const [activityFileKey, setActivityFileKey] = useState(0);

  const loadGalleryContent = async () => {
    try {
      setLoading(true);
      const [aboutRes, starsRes, activitiesRes] = await Promise.all([
        getAboutCarouselSlides(true),
        getShiningStars(true),
        getCampusActivities(true)
      ]);
      setAboutSlides(aboutRes.data);
      setShiningStars(starsRes.data);
      setCampusActivities(activitiesRes.data);
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to load gallery content",
        message: error.response?.data?.error || "Gallery content could not be loaded."
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGalleryContent();
  }, []);

  const handleStarChange = (field, value) => {
    setStarForm((current) => ({ ...current, [field]: value }));
  };

  const handleAboutChange = (field, value) => {
    setAboutForm((current) => ({ ...current, [field]: value }));
  };

  const handleActivityChange = (field, value) => {
    setActivityForm((current) => ({ ...current, [field]: value }));
  };

  const handleImageUpload = async (file, updateForm, resetKey) => {
    try {
      const image = await readGalleryImageFile(file);
      updateForm((current) => ({ ...current, image }));
    } catch (error) {
      showAppToast({ type: "error", title: "Image upload failed", message: error.message });
      resetKey((current) => current + 1);
    }
  };

  const resetAboutForm = () => {
    setEditingAboutId("");
    setAboutForm(createAboutForm());
    setAboutFileKey((current) => current + 1);
  };

  const resetStarForm = () => {
    setEditingStarId("");
    setStarForm(createStarForm());
    setStarFileKey((current) => current + 1);
  };

  const resetActivityForm = () => {
    setEditingActivityId("");
    setActivityForm(createActivityForm());
    setActivityFileKey((current) => current + 1);
  };

  const handleAboutSubmit = async (event) => {
    event.preventDefault();
    if (!aboutForm.image) {
      showAppToast({
        type: "error",
        title: "Carousel image required",
        message: "Please upload an image before adding the carousel slide."
      });
      return;
    }

    try {
      setWorkingKey(editingAboutId || "about-create");
      if (editingAboutId) {
        const response = await updateAboutCarouselSlide(editingAboutId, aboutForm);
        setAboutSlides((current) =>
          current.map((item) => (item._id === editingAboutId ? response.data.slide : item))
        );
        showAppToast({
          type: "success",
          title: "About carousel updated",
          message: "The About page carousel slide has been updated."
        });
      } else {
        const response = await addAboutCarouselSlide(aboutForm);
        setAboutSlides((current) => [response.data.slide, ...current]);
        showAppToast({
          type: "success",
          title: "About carousel created",
          message: "The new About page carousel slide has been added."
        });
      }
      resetAboutForm();
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to save carousel slide",
        message: error.response?.data?.error || "The About carousel slide could not be saved."
      });
    } finally {
      setWorkingKey("");
    }
  };

  const handleStarSubmit = async (event) => {
    event.preventDefault();
    if (!starForm.image) {
      showAppToast({
        type: "error",
        title: "Student image required",
        message: "Please upload an image before adding the shining star."
      });
      return;
    }

    try {
      setWorkingKey(editingStarId || "star-create");
      if (editingStarId) {
        const response = await updateShiningStar(editingStarId, starForm);
        setShiningStars((current) =>
          current.map((item) => (item._id === editingStarId ? response.data.star : item))
        );
        showAppToast({
          type: "success",
          title: "Shining star updated",
          message: "The shining star record has been updated."
        });
      } else {
        const response = await addShiningStar(starForm);
        setShiningStars((current) => [response.data.star, ...current]);
        showAppToast({
          type: "success",
          title: "Shining star created",
          message: "The shining star has been added to gallery content."
        });
      }
      resetStarForm();
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to save shining star",
        message: error.response?.data?.error || "The shining star could not be saved."
      });
    } finally {
      setWorkingKey("");
    }
  };

  const handleActivitySubmit = async (event) => {
    event.preventDefault();
    if (!activityForm.image) {
      showAppToast({
        type: "error",
        title: "Activity image required",
        message: "Please upload an image before adding the campus activity."
      });
      return;
    }

    try {
      setWorkingKey(editingActivityId || "activity-create");
      if (editingActivityId) {
        const response = await updateCampusActivity(editingActivityId, activityForm);
        setCampusActivities((current) =>
          current.map((item) => (item._id === editingActivityId ? response.data.activity : item))
        );
        showAppToast({
          type: "success",
          title: "Campus activity updated",
          message: "The campus activity record has been updated."
        });
      } else {
        const response = await addCampusActivity(activityForm);
        setCampusActivities((current) => [response.data.activity, ...current]);
        showAppToast({
          type: "success",
          title: "Campus activity created",
          message: "The campus activity has been added to gallery content."
        });
      }
      resetActivityForm();
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to save campus activity",
        message: error.response?.data?.error || "The campus activity could not be saved."
      });
    } finally {
      setWorkingKey("");
    }
  };

  const handleEditAbout = (item) => {
    setEditingAboutId(item._id);
    setAboutForm({
      title: item.title || "",
      subtitle: item.subtitle || "",
      description: item.description || "",
      image: item.image || "",
      badge: item.badge || "Bright Line Classes",
      displayOrder: item.displayOrder || 0,
      isActive: item.isActive !== false
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEditStar = (item) => {
    setEditingStarId(item._id);
    setStarForm({
      name: item.name || "",
      image: item.image || "",
      resultLabel: item.resultLabel || "",
      stream: item.stream || "",
      highlight: item.highlight || "",
      posterIndex: item.posterIndex || "",
      displayOrder: item.displayOrder || 0,
      isActive: item.isActive !== false
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEditActivity = (item) => {
    setEditingActivityId(item._id);
    setActivityForm({
      title: item.title || "",
      image: item.image || "",
      description: item.description || "",
      displayOrder: item.displayOrder || 0,
      isActive: item.isActive !== false
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteAbout = async (item) => {
    const confirmed = await confirmAppAction({
      title: "Delete about carousel slide",
      message: `This will remove ${item.title} from the About page carousel.`,
      confirmLabel: "Delete",
      type: "danger"
    });

    if (!confirmed) {
      return;
    }

    try {
      setWorkingKey(item._id);
      await deleteAboutCarouselSlide(item._id);
      setAboutSlides((current) => current.filter((entry) => entry._id !== item._id));
      if (editingAboutId === item._id) {
        resetAboutForm();
      }
      showAppToast({
        type: "success",
        title: "About carousel deleted",
        message: "The carousel slide has been removed."
      });
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to delete carousel slide",
        message: error.response?.data?.error || "The carousel slide could not be removed."
      });
    } finally {
      setWorkingKey("");
    }
  };

  const handleDeleteStar = async (item) => {
    const confirmed = await confirmAppAction({
      title: "Delete shining star",
      message: `This will remove ${item.name} from the public gallery.`,
      confirmLabel: "Delete",
      type: "danger"
    });

    if (!confirmed) {
      return;
    }

    try {
      setWorkingKey(item._id);
      await deleteShiningStar(item._id);
      setShiningStars((current) => current.filter((entry) => entry._id !== item._id));
      if (editingStarId === item._id) {
        resetStarForm();
      }
      showAppToast({
        type: "success",
        title: "Shining star deleted",
        message: "The shining star has been removed."
      });
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to delete shining star",
        message: error.response?.data?.error || "The shining star could not be removed."
      });
    } finally {
      setWorkingKey("");
    }
  };

  const handleDeleteActivity = async (item) => {
    const confirmed = await confirmAppAction({
      title: "Delete campus activity",
      message: `This will remove ${item.title} from the public gallery.`,
      confirmLabel: "Delete",
      type: "danger"
    });

    if (!confirmed) {
      return;
    }

    try {
      setWorkingKey(item._id);
      await deleteCampusActivity(item._id);
      setCampusActivities((current) => current.filter((entry) => entry._id !== item._id));
      if (editingActivityId === item._id) {
        resetActivityForm();
      }
      showAppToast({
        type: "success",
        title: "Campus activity deleted",
        message: "The campus activity has been removed."
      });
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to delete campus activity",
        message: error.response?.data?.error || "The campus activity could not be removed."
      });
    } finally {
      setWorkingKey("");
    }
  };

  const renderImageUpload = (label, value, inputKey, onChange) => (
    <label className="gallery-admin__grid-span-2">
      {label}
      <small className="upload-help">Only JPG and JPEG files are supported.</small>
      <input
        key={inputKey}
        className="gallery-admin__file-input"
        type="file"
        accept=".jpg,.jpeg,image/jpeg"
        onChange={onChange}
      />
      {value && <img src={value} alt={`${label} preview`} className="gallery-admin__image-preview" />}
    </label>
  );

  return (
    <section className="gallery-admin">
      <div className="gallery-admin__header">
        <div>
          <span className="gallery-admin__eyebrow">Dashboard Handle</span>
          <h1>Gallery Management</h1>
          <p>Manage About page carousel, Shining Stars, and Campus Activities without typing image paths.</p>
        </div>
        <button className="gallery-admin__refresh" onClick={loadGalleryContent} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="gallery-admin__stats">
        <article className="gallery-admin__stat-card">
          <span>About Carousel</span>
          <strong>{aboutSlides.length}</strong>
        </article>
        <article className="gallery-admin__stat-card">
          <span>Shining Stars</span>
          <strong>{shiningStars.length}</strong>
        </article>
        <article className="gallery-admin__stat-card">
          <span>Campus Activities</span>
          <strong>{campusActivities.length}</strong>
        </article>
      </div>

      <div className="gallery-admin__forms">
        <form className="gallery-admin__card gallery-admin__form" onSubmit={handleAboutSubmit}>
          <div className="gallery-admin__card-head">
            <h2>{editingAboutId ? "Edit About Carousel" : "Add About Carousel"}</h2>
            {editingAboutId && (
              <button type="button" className="gallery-admin__ghost" onClick={resetAboutForm}>
                Cancel Edit
              </button>
            )}
          </div>
          <div className="gallery-admin__grid">
            <label>
              Title
              <input value={aboutForm.title} onChange={(e) => handleAboutChange("title", e.target.value)} required />
            </label>
            <label>
              Subtitle
              <input value={aboutForm.subtitle} onChange={(e) => handleAboutChange("subtitle", e.target.value)} />
            </label>
            <label>
              Badge
              <input value={aboutForm.badge} onChange={(e) => handleAboutChange("badge", e.target.value)} />
            </label>
            <label>
              Display Order
              <input type="number" value={aboutForm.displayOrder} onChange={(e) => handleAboutChange("displayOrder", e.target.value)} />
            </label>
            {renderImageUpload("Carousel Image", aboutForm.image, aboutFileKey, (e) =>
              handleImageUpload(e.target.files?.[0], setAboutForm, setAboutFileKey)
            )}
            <label className="gallery-admin__toggle">
              <input type="checkbox" checked={aboutForm.isActive} onChange={(e) => handleAboutChange("isActive", e.target.checked)} />
              <span>Active on About page</span>
            </label>
            <label className="gallery-admin__grid-span-2">
              Description
              <textarea value={aboutForm.description} onChange={(e) => handleAboutChange("description", e.target.value)} rows="4" />
            </label>
          </div>
          <button
            className="gallery-admin__submit"
            type="submit"
            disabled={(Boolean(editingAboutId) && workingKey === editingAboutId) || workingKey === "about-create"}
          >
            {editingAboutId ? "Update Carousel Slide" : "Add Carousel Slide"}
          </button>
        </form>

        <form className="gallery-admin__card gallery-admin__form" onSubmit={handleStarSubmit}>
          <div className="gallery-admin__card-head">
            <h2>{editingStarId ? "Edit Shining Star" : "Add Shining Star"}</h2>
            {editingStarId && (
              <button type="button" className="gallery-admin__ghost" onClick={resetStarForm}>
                Cancel Edit
              </button>
            )}
          </div>
          <div className="gallery-admin__grid">
            <label>
              Name
              <input value={starForm.name} onChange={(e) => handleStarChange("name", e.target.value)} required />
            </label>
            <label>
              Poster Index
              <input value={starForm.posterIndex} onChange={(e) => handleStarChange("posterIndex", e.target.value)} />
            </label>
            <label>
              Result Label
              <input value={starForm.resultLabel} onChange={(e) => handleStarChange("resultLabel", e.target.value)} required />
            </label>
            <label>
              Stream
              <input value={starForm.stream} onChange={(e) => handleStarChange("stream", e.target.value)} />
            </label>
            {renderImageUpload("Student Image", starForm.image, starFileKey, (e) =>
              handleImageUpload(e.target.files?.[0], setStarForm, setStarFileKey)
            )}
            <label>
              Display Order
              <input type="number" value={starForm.displayOrder} onChange={(e) => handleStarChange("displayOrder", e.target.value)} />
            </label>
            <label className="gallery-admin__toggle">
              <input type="checkbox" checked={starForm.isActive} onChange={(e) => handleStarChange("isActive", e.target.checked)} />
              <span>Active on public site</span>
            </label>
            <label className="gallery-admin__grid-span-2">
              Highlight
              <textarea value={starForm.highlight} onChange={(e) => handleStarChange("highlight", e.target.value)} rows="4" />
            </label>
          </div>
          <button
            className="gallery-admin__submit"
            type="submit"
            disabled={(Boolean(editingStarId) && workingKey === editingStarId) || workingKey === "star-create"}
          >
            {editingStarId ? "Update Shining Star" : "Add Shining Star"}
          </button>
        </form>

        <form className="gallery-admin__card gallery-admin__form" onSubmit={handleActivitySubmit}>
          <div className="gallery-admin__card-head">
            <h2>{editingActivityId ? "Edit Campus Activity" : "Add Campus Activity"}</h2>
            {editingActivityId && (
              <button type="button" className="gallery-admin__ghost" onClick={resetActivityForm}>
                Cancel Edit
              </button>
            )}
          </div>
          <div className="gallery-admin__grid">
            <label>
              Title
              <input value={activityForm.title} onChange={(e) => handleActivityChange("title", e.target.value)} required />
            </label>
            <label>
              Display Order
              <input type="number" value={activityForm.displayOrder} onChange={(e) => handleActivityChange("displayOrder", e.target.value)} />
            </label>
            {renderImageUpload("Activity Image", activityForm.image, activityFileKey, (e) =>
              handleImageUpload(e.target.files?.[0], setActivityForm, setActivityFileKey)
            )}
            <label className="gallery-admin__toggle">
              <input type="checkbox" checked={activityForm.isActive} onChange={(e) => handleActivityChange("isActive", e.target.checked)} />
              <span>Active on public site</span>
            </label>
            <label className="gallery-admin__grid-span-2">
              Description
              <textarea value={activityForm.description} onChange={(e) => handleActivityChange("description", e.target.value)} rows="4" />
            </label>
          </div>
          <button
            className="gallery-admin__submit"
            type="submit"
            disabled={(Boolean(editingActivityId) && workingKey === editingActivityId) || workingKey === "activity-create"}
          >
            {editingActivityId ? "Update Campus Activity" : "Add Campus Activity"}
          </button>
        </form>
      </div>

      <div className="gallery-admin__lists">
        <section className="gallery-admin__card">
          <div className="gallery-admin__card-head">
            <h2>About Carousel</h2>
          </div>
          <div className="gallery-admin__list">
            {aboutSlides.map((item) => (
              <article key={item._id} className="gallery-admin__list-item">
                <div className="gallery-admin__preview">
                  <img src={item.image} alt={item.title} />
                </div>
                <div className="gallery-admin__copy">
                  <strong>{item.title}</strong>
                  <span>{item.subtitle || item.badge || "About page slide"}</span>
                  <small>{item.isActive === false ? "Inactive" : "Active"} | Order {item.displayOrder || 0}</small>
                </div>
                <div className="gallery-admin__actions">
                  <button className="gallery-admin__action gallery-admin__action--edit" onClick={() => handleEditAbout(item)}>
                    Edit
                  </button>
                  <button
                    className="gallery-admin__action gallery-admin__action--delete"
                    onClick={() => handleDeleteAbout(item)}
                    disabled={workingKey === item._id}
                  >
                    Delete
                  </button>
                </div>
              </article>
            ))}
            {!loading && aboutSlides.length === 0 && <div className="gallery-admin__empty">No About carousel slides added yet.</div>}
          </div>
        </section>

        <section className="gallery-admin__card">
          <div className="gallery-admin__card-head">
            <h2>Shining Stars</h2>
          </div>
          <div className="gallery-admin__list">
            {shiningStars.map((item) => (
              <article key={item._id} className="gallery-admin__list-item">
                <div className="gallery-admin__preview">
                  <img src={item.image} alt={item.name} />
                </div>
                <div className="gallery-admin__copy">
                  <strong>{item.name}</strong>
                  <span>{item.resultLabel}</span>
                  <small>{item.stream || "No stream"} | Order {item.displayOrder || 0}</small>
                </div>
                <div className="gallery-admin__actions">
                  <button className="gallery-admin__action gallery-admin__action--edit" onClick={() => handleEditStar(item)}>
                    Edit
                  </button>
                  <button
                    className="gallery-admin__action gallery-admin__action--delete"
                    onClick={() => handleDeleteStar(item)}
                    disabled={workingKey === item._id}
                  >
                    Delete
                  </button>
                </div>
              </article>
            ))}
            {!loading && shiningStars.length === 0 && <div className="gallery-admin__empty">No shining stars added yet.</div>}
          </div>
        </section>

        <section className="gallery-admin__card">
          <div className="gallery-admin__card-head">
            <h2>Campus Activities</h2>
          </div>
          <div className="gallery-admin__list">
            {campusActivities.map((item) => (
              <article key={item._id} className="gallery-admin__list-item">
                <div className="gallery-admin__preview">
                  <img src={item.image} alt={item.title} />
                </div>
                <div className="gallery-admin__copy">
                  <strong>{item.title}</strong>
                  <span>{item.description || "No description"}</span>
                  <small>Order {item.displayOrder || 0}</small>
                </div>
                <div className="gallery-admin__actions">
                  <button className="gallery-admin__action gallery-admin__action--edit" onClick={() => handleEditActivity(item)}>
                    Edit
                  </button>
                  <button
                    className="gallery-admin__action gallery-admin__action--delete"
                    onClick={() => handleDeleteActivity(item)}
                    disabled={workingKey === item._id}
                  >
                    Delete
                  </button>
                </div>
              </article>
            ))}
            {!loading && campusActivities.length === 0 && <div className="gallery-admin__empty">No campus activities added yet.</div>}
          </div>
        </section>
      </div>
    </section>
  );
}

export default GalleryAdminPage;
